import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Check, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import QuoteWizard from "../components/QuoteWizard";
import { HowItWorks, TrustSignals, WhyUs, Reviews, CTA, SectionHeading } from "../components/sections";
import { api, track } from "../lib/api";
import { HERO_IMG, OG_IMG, TOW_IMG, PRIORITY_CENTRES } from "../data/site";

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    api.get("/content/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/content/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/content/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "AutoDealer"],
    name: "Car To Cash",
    telephone: "+61431206206",
    email: "info@cartocash.com.au",
    url: "https://cartocash.com.au",
    areaServed: [
      { "@type": "GeoCircle", geoMidpoint: { "@type": "GeoCoordinates", latitude: -37.8136, longitude: 144.9631 }, geoRadius: "150000" },
      ...PRIORITY_CENTRES.map(([n]) => ({ "@type": "City", name: n })),
    ],
    priceRange: "$$",
    openingHours: "Mo-Su 07:00-19:00",
    aggregateRating: { "@type": "AggregateRating", ratingValue: settings?.rating || 4.9, reviewCount: settings?.review_count || 380 },
  };

  return (
    <>
      <Seo title="Car To Cash | Cash for Cars Melbourne & Victoria — Free Quote" description="Sell your car for cash today across Melbourne & regional Victoria. Any make, model or condition. Free valuation, 10-minute inspection, paid on the spot, free removal." canonical="https://cartocash.com.au/" image={OG_IMG} jsonLd={jsonLd} />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="hero">
        <div className="absolute inset-0 bg-brand-navy">
          <img src={HERO_IMG} alt="Hands passing green and gold cash across next to a car" loading="eager" className="hidden sm:block h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1E5F2B]/95 via-[#1E5F2B]/82 to-[#1E5F2B]/55" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div className="text-white">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-semibold backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-brand-gold animate-pulse" /> Melbourne &amp; regional Victoria
            </span>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
              {settings?.hero_heading || "Cash for your car, today."}
            </h1>
            <p className="mt-5 text-lg text-white/80 max-w-lg leading-relaxed">
              {settings?.hero_sub || "Any make, model, year or condition. Free valuation, ~10 minute inspection, paid on the spot."}
            </p>
            <ul className="mt-6 space-y-2">
              {["Free, no-obligation valuation", "Paid on the spot — cash or EFT", "Free removal, we come to you"].map((t) => (
                <li key={t} className="flex items-center gap-2 text-white/90"><Check className="h-5 w-5 text-brand-gold" strokeWidth={3} /> {t}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="tel:0431206206" onClick={() => track("phone_tap", { where: "hero" })} className="inline-flex items-center gap-2 rounded-full bg-brand-gold px-7 py-4 font-extrabold text-brand-navy hover:bg-brand-goldDark hover:-translate-y-0.5 transition-all" data-testid="hero-call-btn">
                <Phone className="h-5 w-5" /> 0431 206 206
              </a>
              <a href="#quote" className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 px-7 py-4 font-bold text-white hover:bg-white/10 transition-colors">
                Get a price <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
          <div id="quote" className="scroll-mt-24">
            <QuoteWizard compact />
          </div>
        </div>
      </section>

      <TrustSignals settings={settings} />
      <HowItWorks />

      {/* SERVICES */}
      <section className="bg-brand-surface" data-testid="services-grid">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-20">
          <SectionHeading eyebrow="What we buy" title="Any car, any condition" sub="Every situation is different — tap the one that sounds like yours." />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <Link key={s.slug} to={`/${s.slug}`} className="group rounded-2xl border border-brand-line bg-white overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`service-card-${s.slug}`}>
                <div className="h-40 overflow-hidden">
                  <img src={s.thumbnail} alt={`${s.nav_label} — cash for cars`} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-xl font-extrabold text-brand-navy">{s.nav_label}</h3>
                    <ArrowRight className="h-5 w-5 text-brand-green group-hover:translate-x-1 transition-transform" />
                  </div>
                  <p className="mt-2 text-sm text-brand-muted">{s.card_blurb}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <WhyUs />

      {/* REMOVAL FEATURE */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        <div className="grid lg:grid-cols-2 gap-8 items-center rounded-3xl overflow-hidden border border-brand-line bg-white">
          <div className="h-64 lg:h-full min-h-[280px]"><img src={TOW_IMG} alt="Tow truck lifting a car onto a flatbed" className="h-full w-full object-cover" /></div>
          <div className="p-8 sm:p-12">
            <SectionHeading eyebrow="Included free" title="Free car removal, 7 days a week" sub="Driveable or not, we bring the right gear and tow it away at no cost — the price we quote is the money you keep." />
            <Link to="/car-removals" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-navy px-6 py-3 font-bold text-white hover:bg-brand-navy2 transition-colors" data-testid="removal-learn-btn">
              Learn about removals <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <Reviews testimonials={testimonials} />
      <CTA />
    </>
  );
}
